import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const useAdRotation = (placement: string, autoImpression = true) => {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["ads", placement],
    queryFn: async () => (await api.get(`/ads/${placement}`)).data,
    staleTime: 5 * 60 * 1000,
  });
  const ads = data?.ads || [];
  const [idx, setIdx] = useState(0);
  const ad = ads.length ? ads[idx % ads.length] : null;
  useEffect(() => {
    if (ads.length > 1) {
      const t = setInterval(() => setIdx((i) => i + 1), (data?.rotate_seconds || 15) * 1000);
      return () => clearInterval(t);
    }
  }, [ads.length, data?.rotate_seconds]);
  useEffect(() => {
    if (autoImpression && ad?.id && !user?.is_premium) api.post(`/ads/${ad.id}/impression`).catch(() => {});
  }, [autoImpression, ad?.id, user?.is_premium]);
  return { ad, hidden: !!user?.is_premium };
};

export const isVideoUrl = (u?: string) => /\.(mp4|webm)(\?|$)/i.test(u || "");

export const AdMedia = ({ ad, className, eager = false }: { ad: any; className?: string; eager?: boolean }) =>
  isVideoUrl(ad.image_url) ? (
    <video key={ad.image_url} src={ad.image_url} autoPlay loop muted playsInline preload="auto"
      ref={(el) => { if (el) { el.muted = true; el.defaultMuted = true; } }}
      onCanPlay={(e) => { const v = e.currentTarget; v.muted = true; if (v.paused) v.play().catch(() => {}); }}
      aria-label={ad.name} data-testid="ad-video" className={className} />
  ) : (
    <img src={ad.image_url} alt={ad.name} loading={eager ? "eager" : "lazy"} className={className} />
  );

export const AdLink = ({ ad, className, children, testid }: { ad: any; className: string; children: React.ReactNode; testid?: string }) => {
  const track = () => api.post(`/ads/${ad.id}/click`).catch(() => {});
  return String(ad.target_url || "").startsWith("http") ? (
    <a href={ad.target_url} target="_blank" rel="noopener sponsored" onClick={track}
      className={className} data-testid={testid}>{children}</a>
  ) : (
    <Link to={ad.target_url || "/advertise"} onClick={track} className={className} data-testid={testid}>{children}</Link>
  );
};

export const AdSlot = ({ placement, fill = false }: { placement: string; fill?: boolean }) => {
  const { ad, hidden } = useAdRotation(placement);
  if (hidden || !ad) return null;
  const cls = `block bg-[#121212] border border-[#F59E0B]/60 rounded-xl overflow-hidden hover:border-[#F59E0B] transition-colors ${fill ? "flex-1 flex flex-col" : ""}`;
  return (
    <div data-testid={`ad-slot-${placement}`} className={fill ? "flex-1 flex flex-col" : undefined}>
      <AdLink ad={ad} className={cls} testid="ad-link">
        <AdMedia ad={ad}
          className={fill ? "w-full flex-1 min-h-[250px] object-cover block" : "w-full h-auto block"} />
        <div className="flex items-center justify-between px-3 py-2 border-t border-white/5 shrink-0">
          <span className="font-mono text-[10px] text-gray-500 uppercase tracking-widest truncate">{ad.advertiser}</span>
          <span className="font-mono text-[9px] text-gray-500 uppercase tracking-widest border border-white/15 rounded px-1.5 py-0.5 shrink-0">Ad</span>
        </div>
      </AdLink>
    </div>
  );
};

export const BannerAdSlot = () => {
  const { ad, hidden } = useAdRotation("homepage_banner");
  if (hidden || !ad) return null;
  return (
    <div data-testid="ad-slot-homepage-banner"
      className="group relative rounded-xl overflow-hidden mb-8">
      <span className="absolute top-2 right-2 z-10 font-mono text-[9px] uppercase tracking-widest bg-black/70 text-gray-300 px-1.5 py-0.5 rounded">
        Ad · {ad.advertiser}
      </span>
      <AdLink ad={ad} className="block">
        <AdMedia ad={ad} eager
          className="w-full h-auto max-h-[200px] object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
      </AdLink>
    </div>
  );
};

export const NativeAdCard = ({ placement = "in_feed" }: { placement?: string }) => {
  const { ad, hidden } = useAdRotation(placement);
  if (hidden || !ad) return null;
  const cls = "group flex flex-col h-full bg-[#121212] border border-[#F59E0B]/60 rounded-lg overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:border-[#F59E0B]";
  return (
    <article data-testid={placement === "in_feed" ? "native-ad-card" : `native-ad-card-${placement}`} className="h-full">
      <AdLink ad={ad} className={cls}>
        <div className="relative aspect-[16/9] overflow-hidden bg-[#1A1A1A]">
          <AdMedia ad={ad}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
          <span className="absolute bottom-3 left-0 z-10 bg-[#241c08]/95 border-l-[3px] border-[#F59E0B] px-3 py-1.5 font-heading font-bold text-[#FBBF24] text-[10px] tracking-[0.18em] uppercase shadow-lg">
            Sponsored
          </span>
        </div>
        <div className="p-4 sm:p-5 flex flex-col flex-1">
          <h3 className="font-heading font-semibold text-white text-base leading-snug line-clamp-2 group-hover:text-[#00E5FF] transition-colors">
            {ad.name}
          </h3>
          <div className="flex items-center justify-between gap-2 mt-auto pt-3">
            <p className="font-mono text-[10px] text-gray-500 uppercase tracking-wide truncate">{ad.advertiser}</p>
            <span className="font-mono text-[9px] text-gray-500 uppercase tracking-widest border border-white/15 rounded px-1.5 py-0.5 shrink-0">Ad</span>
          </div>
        </div>
      </AdLink>
    </article>
  );
};

/* Compact native strip for the mega-menu panels (Markets / Sectors / Latest).
   One shared rotation: placement "nav_menu". */
export const NavMenuAd = ({ className }: { className?: string }) => {
  const { ad, hidden } = useAdRotation("nav_menu");
  if (hidden || !ad) return null;
  return (
    <div data-testid="ad-slot-nav-menu" className={className || "col-span-full mt-1 pt-4 border-t border-white/[0.07]"}>
      <AdLink ad={ad}
        className="group flex items-center gap-3.5 rounded-lg border border-[#F59E0B]/35 bg-white/[0.02] px-3 py-2.5 hover:border-[#F59E0B]/80 transition-colors">
        <AdMedia ad={ad} className="w-24 aspect-video object-cover rounded shrink-0" />
        <span className="min-w-0 flex-1">
          <span className="block font-mono text-[9px] text-[#FBBF24] uppercase tracking-[0.18em] font-bold">Sponsored</span>
          <span className="block text-[13px] font-semibold text-white leading-snug line-clamp-1 group-hover:text-[#00E5FF] transition-colors mt-0.5">
            {ad.name}
          </span>
          <span className="block font-mono text-[10px] text-gray-500 uppercase tracking-wide truncate mt-0.5">{ad.advertiser}</span>
        </span>
        <span className="font-mono text-[9px] text-gray-500 uppercase tracking-widest border border-white/15 rounded px-1.5 py-0.5 shrink-0">Ad</span>
      </AdLink>
    </div>
  );
};

export const ArticleInlineAd = () => {
  const { ad, hidden } = useAdRotation("article_inline");
  if (hidden || !ad) return null;
  return (
    <aside data-testid="article-inline-ad" className="not-prose my-8">
      <AdLink ad={ad}
        className="group flex gap-4 items-center bg-[#121212] border border-[#F59E0B]/60 rounded-lg overflow-hidden p-3 sm:p-4 hover:border-[#F59E0B] transition-colors">
        <AdMedia ad={ad}
          className="w-28 sm:w-40 aspect-video object-cover rounded shrink-0" />
        <div className="min-w-0 flex-1">
          <span className="font-mono text-[9px] text-[#FBBF24] uppercase tracking-[0.18em] font-bold">Sponsored</span>
          <h4 className="font-heading font-semibold text-white text-sm sm:text-base leading-snug line-clamp-2 mt-1 group-hover:text-[#00E5FF] transition-colors">
            {ad.name}
          </h4>
          <p className="font-mono text-[10px] text-gray-500 uppercase tracking-wide truncate mt-1.5">{ad.advertiser}</p>
        </div>
        <span className="font-mono text-[9px] text-gray-500 uppercase tracking-widest border border-white/15 rounded px-1.5 py-0.5 shrink-0 self-start">Ad</span>
      </AdLink>
    </aside>
  );
};
